import { useState } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

import { CardShell } from '@shared/ui/CardShell';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Loader } from '@shared/ui/Progress';
import { IconRefresh } from '@shared/ui/icons';
import {
  apiErrorText,
  useGetCounterpartyDetailsQuery,
  useListContractsQuery,
  useListClaimsQuery,
  useListLawsuitsQuery,
  useListCorrespondenceQuery,
  useRefreshCounterpartyEgrulMutation,
  useUpdateCounterpartyNotesMutation,
} from '@shared/api';
import {
  contractStatusLabel,
  claimStatusLabel,
  lawsuitStatusLabel,
  lawsuitStatusTone,
  correspondenceStatusLabel,
  counterpartyStatusLabel,
  counterpartyStatusTone,
} from '@shared/lib/statusLabels';
import {
  lawsuitClaimLabel,
  type Counterparty,
} from '@entities/legal/types';
import { useAgentPermission } from '@entities/user';

import { ExternalRegistries } from './ExternalRegistries';
import { RiskMap } from './RiskMap';
import { RequisiteCheck } from './RequisiteCheck';
import { CorrespondenceCard } from './CorrespondenceCard';
import { CounterpartyDocumentsSection } from './CounterpartyDocumentsSection';

// — страница «всё по контрагенту». Заменяет модалку CounterpartyCard:
// реквизиты + карта рисков + все связанные сущности (договоры, претензии,
// иски, переписка) на одном URL `/agents/statut/counterparties/:id`.
// Каждая связанная сущность — кликабельная строка, ведёт на свою карточку.

const Grid = styled.dl`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
  margin: 0;
  font-size: 14px;

  & > dt {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  & > dd {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  /* На узких экранах двухколоночная сетка ломается — складываем в одну. */
  @media (max-width: 540px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing(1)} 0;

    & > dt {
      margin-top: ${({ theme }) => theme.spacing(2)};
      font-size: 12px;
    }
  }
`;

const Section = styled.section`
  margin-top: ${({ theme }) => theme.spacing(6)};

  & > h4 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.secondary};
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
    flex-wrap: wrap;
  }
`;

const Count = styled.span`
  font-weight: 600;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  letter-spacing: 0;
`;

const Empty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-style: italic;
`;

/** Кликабельная строка связанной сущности. Это `<button>` — фокус-кольцо
 *  (shadow.focus через :focus-visible) и подавление анимаций при
 *  prefers-reduced-motion наследуются из GlobalStyle, дублировать не нужно. */
const LinkRow = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  width: 100%;
  text-align: left;
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(2)};
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.sm};
  transition: background-color ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const RowMain = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowMuted = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12px;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(3)};

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Stat = styled.div`
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  text-align: center;

  & > .num {
    font-size: 24px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text.primary};
  }
  & > .lab {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
    margin-top: 4px;
  }
`;

const DangerBanner = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.status.danger}22;
  border: 1px solid ${({ theme }) => theme.colors.status.danger}66;
  border-left: 3px solid ${({ theme }) => theme.colors.status.danger};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  line-height: 1.5;
`;

const WarnBanner = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.status.warning}22;
  border: 1px solid ${({ theme }) => theme.colors.status.warning}66;
  border-left: 3px solid ${({ theme }) => theme.colors.status.warning};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.colors.status.warning};
  font-size: 13px;
  line-height: 1.5;
`;

const InfoBanner = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.status.info}18;
  border: 1px solid ${({ theme }) => theme.colors.status.info}55;
  border-left: 3px solid ${({ theme }) => theme.colors.status.info};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.colors.status.info};
  font-size: 13px;
  line-height: 1.5;
`;

/** Ссылка-переход к секции сверки внутри баннера. Наследует цвет баннера
 *  (currentColor), подчёркнута. Фокус-кольцо (shadow.focus) наследуется из
 *  GlobalStyle через :focus-visible. */
const BannerLink = styled.button`
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const Mono = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
`;

const MutedInline = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12px;
  margin-left: ${({ theme }) => theme.spacing(2)};
`;

const Toolbar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
  align-items: center;
`;

const ErrorText = styled.span`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  align-self: center;
`;

const NotesArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  line-height: 1.45;
  resize: vertical;
`;

const NotesActions = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const RefreshIconWrap = styled.span<{ $spinning: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${({ $spinning }) =>
    $spinning &&
    css`
      animation: ${spin} 900ms linear infinite;
    `}
`;

function fmtDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('ru-RU');
}

const SECTION_LIMIT = 100;

export function CounterpartyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  if (!id) {
    return <Navigate to="/agents/statut/counterparties" replace />;
  }

  return <CounterpartyView id={id} onBack={() => navigate('/agents/statut/counterparties')} />;
}

interface ViewProps {
  id: string;
  onBack: () => void;
}

function CounterpartyView({ id, onBack }: ViewProps) {
  const navigate = useNavigate();
  const { can_write } = useAgentPermission('statut');
  const { data, isLoading } = useGetCounterpartyDetailsQuery(id);

  if (isLoading || !data) {
    return (
      <CardShell title="Карточка контрагента" onBack={onBack}>
        <Loader />
      </CardShell>
    );
  }

  const c = data.counterparty;
  const subtitle = [c.inn && `ИНН ${c.inn}`, c.legal_form].filter(Boolean).join(' · ');

  return (
    <CardShell
      title={c.short_name ?? c.full_name}
      subtitle={subtitle || undefined}
      onBack={onBack}
    >
      <CounterpartyBody
        counterparty={c}
        egrulEnabled={data.egrul_enabled}
        contractsCount={data.contracts_count}
        claimsCount={data.claims_count}
        lawsuitsCount={data.lawsuits_count}
        canWrite={can_write}
        onOpenContract={(cid) => navigate(`/agents/statut/contracts/${cid}`)}
        onOpenClaim={(clid) => navigate(`/agents/statut/claims/${clid}`)}
        onOpenLawsuit={(lid) => navigate(`/agents/statut/lawsuits/${lid}`)}
      />
    </CardShell>
  );
}

interface BodyProps {
  counterparty: Counterparty;
  egrulEnabled: boolean;
  contractsCount: number;
  claimsCount: number;
  lawsuitsCount: number;
  canWrite: boolean;
  onOpenContract: (id: string) => void;
  onOpenClaim: (id: string) => void;
  onOpenLawsuit: (id: string) => void;
}

function CounterpartyBody({
  counterparty: c,
  egrulEnabled,
  contractsCount,
  claimsCount,
  lawsuitsCount,
  canWrite,
  onOpenContract,
  onOpenClaim,
  onOpenLawsuit,
}: BodyProps) {
  const id = c.id;
  const [refreshEgrul, { isLoading: isRefreshing }] = useRefreshCounterpartyEgrulMutation();
  const [updateNotes, { isLoading: isSavingNotes }] = useUpdateCounterpartyNotesMutation();
  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [openCorrId, setOpenCorrId] = useState<string | null>(null);
  // id документа, к которому нужно проскроллить в секции «Документы
  // контрагента» — ставится при клике на «Документ-источник» в сверке.
  const [focusDocId, setFocusDocId] = useState<string | null>(null);

  // Быстрая отметка расхождений реквизитов из risk_flags (без отдельного
  // запроса) — управляет верхним индикатором и подсказкой в секции сверки.
  const reqChanges = c.risk_flags?.requisite_changes;
  const hasReqChanges = reqChanges?.has_changes === true;
  const reqWarnings = reqChanges?.warning_count ?? 0;

  // Скролл к секции сверки по ссылке из баннера. Плавность берётся из
  // html { scroll-behavior: smooth } и автоматически отключается при
  // prefers-reduced-motion (GlobalStyle) — behavior здесь не задаём.
  function scrollToRequisites() {
    const el = document.getElementById(`cp-requisites-${id}`);
    if (!el) return;
    el.scrollIntoView({ block: 'start' });
    // Перенос фокуса для клавиатуры/скринридера; кольца не будет —
    // программный фокус не триггерит :focus-visible.
    el.focus();
  }

  const contractsQ = useListContractsQuery({ counterparty_id: id, limit: SECTION_LIMIT });
  const claimsAllQ = useListClaimsQuery({ counterparty_id: id, limit: SECTION_LIMIT });
  const lawsuitsQ = useListLawsuitsQuery({ counterparty_id: id, limit: SECTION_LIMIT });
  const corrQ = useListCorrespondenceQuery({ counterparty_id: id, limit: SECTION_LIMIT });

  const contracts = contractsQ.data?.items ?? [];
  // Реестр претензий и иски хранятся в одной таблице; на странице
  // контрагента показываем претензии (claim) и иски-сущности (lawsuit)
  // раздельно. claim_type='lawsuit' в claims — это «иск как делопроизводство»,
  // его оставляем в блоке претензий вместе с claim.
  const claims = claimsAllQ.data?.items ?? [];
  const lawsuits = lawsuitsQ.data?.items ?? [];
  const correspondence = corrQ.data?.items ?? [];

  const isLiquidated = c.status === 'LIQUIDATED' || c.status === 'BANKRUPT';
  const isAtRisk = c.status === 'LIQUIDATING' || c.status === 'REORGANIZING';
  const isUnknown = c.status === 'UNKNOWN' || !c.last_checked_at;

  async function onRefresh() {
    setRefreshError(null);
    try {
      await refreshEgrul(id).unwrap();
    } catch (e) {
      setRefreshError(apiErrorText(e, 'Не удалось обновить из ЕГРЮЛ'));
    }
  }

  const currentNotes = notesDraft ?? c.notes ?? '';
  const notesChanged = notesDraft !== null && notesDraft !== (c.notes ?? '');

  async function onSaveNotes() {
    await updateNotes({
      id,
      notes: (notesDraft ?? '').trim() || null,
    }).unwrap();
    setNotesDraft(null);
  }

  return (
    <>
      {isLiquidated && (
        <DangerBanner>
          <strong>Контрагент {counterpartyStatusLabel(c.status).toLowerCase()}.</strong> Заключение
          новых договоров с ним крайне рискованно. Если есть действующие договоры — рассмотрите
          досрочное расторжение.
          {c.liquidation_date && (
            <>
              {' '}
              Дата прекращения деятельности: <strong>{fmtDate(c.liquidation_date)}</strong>.
            </>
          )}
        </DangerBanner>
      )}
      {isAtRisk && (
        <WarnBanner>
          <strong>Контрагент {counterpartyStatusLabel(c.status).toLowerCase()}.</strong> Перед
          заключением новых договоров уточните статус процедуры и полномочия подписанта.
        </WarnBanner>
      )}
      {isUnknown && egrulEnabled && (
        <WarnBanner>
          Контрагент ещё не сверялся с ЕГРЮЛ. Рекомендуем обновить данные — кнопка ниже.
        </WarnBanner>
      )}
      {hasReqChanges &&
        (reqWarnings > 0 ? (
          <WarnBanner>
            Обнаружены расхождения реквизитов: <strong>{reqWarnings}</strong>{' '}
            {reqWarnings === 1 ? 'важное' : 'важных'}, {reqChanges?.info_count ?? 0}{' '}
            информационных.{' '}
            <BannerLink type="button" onClick={scrollToRequisites}>
              Проверьте в разделе «Сверка реквизитов»
            </BannerLink>
            .
          </WarnBanner>
        ) : (
          <InfoBanner>
            Есть информационные расхождения реквизитов: {reqChanges?.info_count ?? 0}.{' '}
            <BannerLink type="button" onClick={scrollToRequisites}>
              Посмотреть в разделе «Сверка реквизитов»
            </BannerLink>
            .
          </InfoBanner>
        ))}

      <Toolbar>
        {egrulEnabled && (
          <Button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing || !canWrite}
            title={!canWrite ? 'Только чтение' : undefined}
          >
            <RefreshIconWrap $spinning={isRefreshing}>
              <IconRefresh size={16} />
            </RefreshIconWrap>
            {isRefreshing ? 'Обновляем из ЕГРЮЛ…' : 'Обновить из ЕГРЮЛ'}
          </Button>
        )}
        {refreshError && <ErrorText>{refreshError}</ErrorText>}
      </Toolbar>

      <Section>
        <h4>Реквизиты</h4>
        <Grid>
          <dt>ИНН</dt>
          <dd>
            <Mono>{c.inn}</Mono>
          </dd>
          {c.kpp && (
            <>
              <dt>КПП</dt>
              <dd>
                <Mono>{c.kpp}</Mono>
              </dd>
            </>
          )}
          {c.ogrn && (
            <>
              <dt>ОГРН</dt>
              <dd>
                <Mono>{c.ogrn}</Mono>
              </dd>
            </>
          )}
          <dt>Полное наименование</dt>
          <dd>{c.full_name}</dd>
          {c.short_name && c.short_name !== c.full_name && (
            <>
              <dt>Краткое</dt>
              <dd>{c.short_name}</dd>
            </>
          )}
          <dt>ОПФ</dt>
          <dd>{c.legal_form ?? '—'}</dd>
          <dt>Адрес</dt>
          <dd>{c.address ?? '—'}</dd>
          <dt>Руководитель</dt>
          <dd>
            {c.director_name ? (
              <>
                {c.director_name}
                {c.director_position && <MutedInline>· {c.director_position}</MutedInline>}
              </>
            ) : (
              '—'
            )}
          </dd>
          <dt>Дата регистрации</dt>
          <dd>{fmtDate(c.registration_date)}</dd>
          {c.liquidation_date && (
            <>
              <dt>Дата ликвидации</dt>
              <dd>{fmtDate(c.liquidation_date)}</dd>
            </>
          )}
          <dt>Статус</dt>
          <dd>
            <Badge $tone={counterpartyStatusTone(c.status)}>
              {counterpartyStatusLabel(c.status)}
            </Badge>
          </dd>
          <dt>Последняя проверка</dt>
          <dd>
            {c.last_checked_at
              ? new Date(c.last_checked_at).toLocaleString('ru-RU')
              : 'не проверялся'}
          </dd>
        </Grid>
      </Section>

      <Section id={`cp-requisites-${id}`} tabIndex={-1}>
        <h4>Сверка реквизитов</h4>
        <RequisiteCheck
          counterpartyId={id}
          knownChanges={reqChanges}
          onOpenDocument={setFocusDocId}
        />
      </Section>

      <Section>
        <h4>Активность</h4>
        <Stats>
          <Stat>
            <div className="num">{contractsCount}</div>
            <div className="lab">Договоров</div>
          </Stat>
          <Stat>
            <div className="num">{claimsCount}</div>
            <div className="lab">Претензий</div>
          </Stat>
          <Stat>
            <div className="num">{lawsuitsCount}</div>
            <div className="lab">Исков</div>
          </Stat>
        </Stats>
      </Section>

      {/* Свой сворачиваемый заголовок «Документы контрагента (N)» —
          отдельная страничная <Section> с h4 не нужна (был бы двойной
          заголовок), рендерим напрямую как ClaimDocumentsSection в карточке
          претензии. */}
      <CounterpartyDocumentsSection counterpartyId={id} focusDocId={focusDocId} />

      <Section>
        <h4>Внешние реестры</h4>
        <ExternalRegistries counterparty={c} />
      </Section>

      {contractsCount > 0 && (
        <Section>
          <h4>Карта рисков по контрагенту</h4>
          <RiskMap counterpartyId={id} />
        </Section>
      )}

      <Section>
        <h4>
          Договоры <Count>{contractsQ.isLoading ? '…' : contracts.length}</Count>
        </h4>
        {contractsQ.isLoading ? (
          <Loader />
        ) : contracts.length === 0 ? (
          <Empty>Нет договоров с этим контрагентом.</Empty>
        ) : (
          contracts.map((it) => (
            <LinkRow
              key={it.id}
              type="button"
              onClick={() => onOpenContract(it.id)}
              title="Открыть карточку договора"
            >
              <RowMain>{it.subject ?? it.counterparty}</RowMain>
              <Badge>{contractStatusLabel(it.status)}</Badge>
            </LinkRow>
          ))
        )}
      </Section>

      <Section>
        <h4>
          Претензии и иски <Count>{claimsAllQ.isLoading ? '…' : claims.length}</Count>
        </h4>
        {claimsAllQ.isLoading ? (
          <Loader />
        ) : claims.length === 0 ? (
          <Empty>Нет претензий и исков по этому контрагенту.</Empty>
        ) : (
          claims.map((it) => (
            <LinkRow
              key={it.id}
              type="button"
              onClick={() => onOpenClaim(it.id)}
              title="Открыть карточку претензии / иска"
            >
              <Badge>{it.claim_type === 'lawsuit' ? 'иск' : 'претензия'}</Badge>
              <RowMain>
                {it.dispute_type ?? '—'}
                {it.amount != null && (
                  <RowMuted> · {it.amount.toLocaleString('ru-RU')} ₽</RowMuted>
                )}
              </RowMain>
              <Badge>{claimStatusLabel(it.stage)}</Badge>
            </LinkRow>
          ))
        )}
      </Section>

      <Section>
        <h4>
          Исковые заявления <Count>{lawsuitsQ.isLoading ? '…' : lawsuits.length}</Count>
        </h4>
        {lawsuitsQ.isLoading ? (
          <Loader />
        ) : lawsuits.length === 0 ? (
          <Empty>Нет сформированных исковых заявлений.</Empty>
        ) : (
          lawsuits.map((it) => (
            <LinkRow
              key={it.id}
              type="button"
              onClick={() => onOpenLawsuit(it.id)}
              title="Открыть карточку иска"
            >
              <RowMain>{it.title || lawsuitClaimLabel(it)}</RowMain>
              <Badge $tone={lawsuitStatusTone(it.status)}>{lawsuitStatusLabel(it.status)}</Badge>
            </LinkRow>
          ))
        )}
      </Section>

      <Section>
        <h4>
          Корреспонденция <Count>{corrQ.isLoading ? '…' : correspondence.length}</Count>
        </h4>
        {corrQ.isLoading ? (
          <Loader />
        ) : correspondence.length === 0 ? (
          <Empty>Нет писем по этому контрагенту.</Empty>
        ) : (
          correspondence.map((it) => (
            <LinkRow
              key={it.id}
              type="button"
              onClick={() => setOpenCorrId(it.id)}
              title="Открыть письмо"
            >
              <RowMuted>{fmtDate(it.incoming_at)}</RowMuted>
              <RowMain>
                {it.subject ?? '(без темы)'}
                {it.sender && <RowMuted> · {it.sender}</RowMuted>}
              </RowMain>
              <Badge>{correspondenceStatusLabel(it.status)}</Badge>
            </LinkRow>
          ))
        )}
      </Section>

      <Section>
        <h4>Заметки юриста</h4>
        <NotesArea
          value={currentNotes}
          onChange={(e) => setNotesDraft(e.target.value)}
          placeholder="Внутренние заметки по контрагенту: контакты, особенности работы, история."
          aria-label="Заметки юриста по контрагенту"
        />
        {notesChanged && (
          <NotesActions>
            <Button
              type="button"
              $size="sm"
              onClick={onSaveNotes}
              disabled={isSavingNotes || !canWrite}
              title={!canWrite ? 'Только чтение' : undefined}
            >
              {isSavingNotes ? 'Сохраняем…' : 'Сохранить заметки'}
            </Button>
            <Button
              type="button"
              $size="sm"
              $variant="secondary"
              onClick={() => setNotesDraft(null)}
            >
              Отменить
            </Button>
          </NotesActions>
        )}
      </Section>

      {openCorrId && (
        <CorrespondenceCard correspondenceId={openCorrId} onClose={() => setOpenCorrId(null)} />
      )}
    </>
  );
}
